import React from 'react';
import StatCounter from '../components/StatCounter';
import { Helmet } from 'react-helmet-async';
import CtaStrip from '../components/CtaStrip';
import Footer from '../components/Footer';

const expertise = [
  { module: 'D365 Customer Engagement (CE/CRM)', level: 'Expert', years: '8 yrs', color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' },
  { module: 'Power Platform (Automate, PVA, PCF)', level: 'Expert', years: '6 yrs', color: 'var(--ms-teal)', bg: '#DFF6F0' },
  { module: 'D365 Finance & Operations', level: 'Proficient', years: '4 yrs', color: 'var(--ms-purple)', bg: '#F4F0FF' },
  { module: 'Azure Integration (Functions, Service Bus)', level: 'Proficient', years: '5 yrs', color: 'var(--ms-orange)', bg: '#FFF1EC' },
];

const specialisations = [
  { icon: '🔌', label: 'Plugin Development' },
  { icon: '📜', label: 'Web Resources & Form Scripts' },
  { icon: '🧩', label: 'PCF Controls' },
  { icon: '🤖', label: 'PVA Chatbots' },
  { icon: '⚡', label: 'Power Automate' },
  { icon: '🔗', label: 'Third-Party Integrations' },
  { icon: '📦', label: 'Data Migration' },
  { icon: '🛠️', label: 'CRM Support & Maintenance' },
];

const values = [
  { title: 'Direct Access', desc: 'You deal with me directly — not an account manager or a support portal. I\'m the person writing the code.' },
  { title: 'Honest Scoping', desc: 'I tell you what can realistically be built, how long it will take, and what it will cost — before any work begins.' },
  { title: 'Clean Deliverables', desc: 'Every plugin, flow, and customisation is tested, documented, and handed over in a managed solution that you own.' },
  { title: 'Post-Go-Live Support', desc: 'I don\'t disappear after delivery. Questions, tweaks, and post-launch issues are handled promptly.' },
];

const faqs = [
  {
    q: 'Do you work with clients outside Pakistan?',
    a: 'Yes — all of my clients are international. The majority are based in the UK, Europe, the US, and Australia. All work is delivered remotely via calls, shared environments, and documentation.',
  },
  {
    q: 'What D365 versions do you work with?',
    a: 'I work with Dynamics 365 Customer Engagement (Sales, Customer Service, Field Service), Dynamics 365 Finance & Operations, Power Platform (Power Automate, Power Apps, Power Virtual Agents / Copilot Studio), and Dataverse.',
  },
  {
    q: 'How do you handle project scoping and pricing?',
    a: 'I don\'t quote before I understand the requirement. Send me the details of what you need and I\'ll come back with a fixed scope, a fixed price, and a realistic timeline — no surprises.',
  },
  {
    q: 'Can you work within our existing D365 environment?',
    a: 'Yes. Most of my work is with businesses that already have D365 deployed and need customisation, support, or new features added to their existing setup.',
  },
];

export default function AboutPage({ onNavigate }) {
  return (
    <div>
      <Helmet>
        <title>About AdjustD365 — Experienced Dynamics 365 Developer & Consultant</title>
        <meta name="description" content="8+ years of Dynamics 365 development experience. Specialising in D365 plugins, PCF controls, Power Automate, PVA chatbots, and CRM integrations. Direct, fast, enterprise-quality delivery." />
        <link rel="canonical" href="https://adjustd365.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          'name': 'AdjustD365 Consultant',
          'jobTitle': 'Dynamics 365 Developer & Consultant',
          'worksFor': { '@type': 'Organization', 'name': 'AdjustD365', 'url': 'https://adjustd365.com' },
          'url': 'https://adjustd365.com/about',
          'knowsAbout': ['Dynamics 365', 'Power Platform', 'CRM Development', 'Plugin Development', 'PCF Controls', 'Power Automate', 'PVA Chatbots'],
          'hasOccupation': {
            '@type': 'Occupation',
            'name': 'Dynamics 365 Consultant',
            'description': 'Custom D365 development including plugins, PCF controls, Power Automate flows, PVA chatbots, and third-party integrations.',
          },
        })}</script>
      </Helmet>

      {/* PAGE HERO */}
      <div className="section-pad" style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '56px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-s)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Home</button>
            <span>›</span>
            <span style={{ color: 'var(--ms-blue)', fontWeight: 500 }}>About</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '48px', alignItems: 'center' }} className="hero-grid">
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>About AdjustD365</div>
              <h1 style={{ fontSize: 'clamp(26px,3vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '16px' }}>
                Microsoft Dynamics 365 Developer & Consultant
              </h1>
              <p style={{ fontSize: '17px', color: 'var(--text-m)', lineHeight: 1.7, maxWidth: '560px', marginBottom: '28px' }}>
                A freelance Dynamics 365 developer with over 8 years of experience delivering D365 customisations, integrations, and solutions for businesses across the UK, Europe, and beyond.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button onClick={() => onNavigate('contact')} style={{ background: 'var(--ms-blue)', color: '#fff', padding: '12px 28px', borderRadius: '4px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                  Get in Touch
                </button>
                <button onClick={() => onNavigate('services')} style={{ background: 'transparent', color: 'var(--ms-blue)', padding: '12px 28px', borderRadius: '4px', fontSize: '14px', fontWeight: 600, border: '1px solid var(--ms-blue)', cursor: 'pointer' }}>
                  View Services
                </button>
              </div>
            </div>
            {/* Stats card */}
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '28px', minWidth: '220px' }}>
              {[
                { target: 96, suffix: '+',     staticVal: null,    label: 'Happy Customers' },
                { target: 0,  suffix: '',      staticVal: '★ 5.0', label: 'Client Rating' },
                { target: 8,  suffix: '+',     staticVal: null,    label: 'Years D365 Exp.' },
                { target: 24, suffix: 'h',     staticVal: null,    label: 'Response Time', prefix: '< ' },
              ].map((s, i) => (
                <div key={i} style={{ padding: '14px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                  <StatCounter
                    target={s.target}
                    prefix={s.prefix || ''}
                    suffix={s.suffix}
                    staticVal={s.staticVal}
                    label={s.label}
                    duration={1600}
                    numStyle={{ fontSize: '22px', letterSpacing: '-0.02em' }}
                    labStyle={{ fontSize: '12px', color: 'var(--text-s)', marginTop: '2px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* BIO SECTION */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'start' }} className="hero-grid">
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>Background</div>
            <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '20px' }}>
              8 Years Building on the Microsoft Stack
            </h2>
            <p style={{ fontSize: '15px', color: 'var(--text-m)', lineHeight: 1.8, marginBottom: '16px' }}>
              I started working with Dynamics CRM in 2016, when it was still on version 8. Since then I've worked across every major version through to Dynamics 365 CE (2025 Wave), Finance & Operations, and the full Power Platform — on everything from greenfield implementations to complex legacy migrations.
            </p>
            <p style={{ fontSize: '15px', color: 'var(--text-m)', lineHeight: 1.8, marginBottom: '16px' }}>
              My background is software development first, consulting second. That means every solution I deliver is built the way a developer would want it: clean architecture, proper error handling, tested in a sandbox before production, and documented so someone else can maintain it without calling me.
            </p>
            <p style={{ fontSize: '15px', color: 'var(--text-m)', lineHeight: 1.8 }}>
              I've worked with clients in financial services, logistics, healthcare, professional services, and retail — each with different D365 deployments and different requirements. That range of exposure means I've almost certainly encountered your specific problem before.
            </p>
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '16px' }}>D365 Expertise</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {expertise.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bg-2)', borderRadius: '4px', borderLeft: `3px solid ${e.color}` }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-h)' }}>{e.module}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-s)', marginTop: '2px' }}>{e.years}</div>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: e.color, background: e.bg, whiteSpace: 'nowrap' }}>{e.level}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPECIALISATIONS */}
      <section className="section-pad" style={{ padding: '0 48px 80px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>Specialisations</div>
          <h2 style={{ fontSize: 'clamp(20px,2.2vw,28px)', fontWeight: 700, color: 'var(--text-h)', marginBottom: '24px' }}>What I build</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px' }} className="grid-4col">
            {specialisations.map((s, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>{s.icon}</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-h)' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW I WORK */}
      <section className="section-pad" style={{ padding: '80px 48px', background: 'var(--bg-2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>How I Work</div>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '36px' }}>What working with me looks like</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px' }} className="hero-grid">
            {values.map((v, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '28px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '8px' }}>{v.title}</div>
                <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.7 }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>FAQ</div>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '36px' }}>Common Questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ padding: '24px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '8px' }}>{f.q}</div>
                <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.7, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaStrip
        heading="Ready to work together?"
        subtext="Tell me about your D365 requirement and I'll come back with a clear plan within 24 hours."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
